const SLIDER_SELECTORS = {
  productSlideshow: '[data-product-slideshow]',
  productThumbs: '[data-product-thumbs]',
  dataTallLayout: 'data-tall-layout',
  mediaType: 'data-type',
  dataMediaId: 'data-media-id',
  dataGroupId: 'data-group-id',
  deferredMediaButton: '[data-deferred-media-button]',
  productSlide: '.product__slide',
  productThumb: '[data-product-thumb]',
  classHidden: 'hidden',
  classMediaHidden: 'media--hidden',
  classThumbsHidden: 'product__thumbs--hidden',
  sliderEnabled: 'is-initialized',
  focusEnabled: 'is-focused',
  splideList: '.splide__list',
  splideStash: '[data-splide-stash]',
  dataSlideOrder: 'data-slide-order',
};

const SLIDER_CLASSES = {
  classDrag: 'is-dragging',
};

const ZOOM_SELECTORS = {
  zoomWrapper: '[data-zoom-wrapper]',
  dataImageSrc: 'data-image-src',
  dataImageWidth: 'data-image-width',
  dataImageHeight: 'data-image-height',
  dataImageZoomEnable: 'data-image-zoom-enable',
  thumbs: '.pswp__thumbs',
  caption: '[data-zoom-caption]',
};

const ZOOM_CLASSES = {
  variantSoldOut: 'variant--soldout',
  variantUnavailable: 'variant--unavailabe',
  popupThumb: 'pswp__thumb',
  popupClass: 'pswp-zoom-gallery',
  popupClassNoThumbs: 'pswp-zoom-gallery--single',
  popupTitle: 'product__title',
  popupTitleNew: 'product__title pswp__title',
};

const VIDEO_HOSTS = {
  html5: 'html5',
  youtube: 'youtube',
};

const VIDEO_SELECTORS = {
  deferredMedia: '[data-deferred-media]',
  deferredMediaButton: '[data-deferred-media-button]',
  productMediaWrapper: '[data-product-single-media-wrapper]',
  mediaContainer: '[data-video]',
  mediaId: 'data-media-id',
};

const MODEL_SELECTORS = {
  modelViewer: '[data-model]',
  classMediaHidden: 'media--hidden',
};

const THUMB_CLASSES = {
  classActive: 'is-active',
};

/** Thumb rail synced to main Splide; visibility follows the main track after variant filtering. */
class ProductMediaThumbs {
  constructor({ splide, host }) {
    this.splide = splide;
    this.host = host;
    this.items = host ? Array.from(host.querySelectorAll(SLIDER_SELECTORS.productThumb)) : [];
    this._onSplideMoved = () => this.syncFromSplide();
    this._onClick = this._handleClick.bind(this);

    if (!host || !splide) return;

    host.addEventListener('click', this._onClick);
    splide.on('moved', this._onSplideMoved);
    splide.on('mounted', this._onSplideMoved);
    this.syncFromSplide();
  }

  _getSplideSlides() {
    return this.splide?.Components?.Slides?.get() || [];
  }

  _mediaIdFromSplideSlide(slideEl) {
    if (!slideEl) return null;

    return (
      slideEl.getAttribute(SLIDER_SELECTORS.dataMediaId) ||
      slideEl.querySelector(`[${SLIDER_SELECTORS.dataMediaId}]`)?.getAttribute(SLIDER_SELECTORS.dataMediaId) ||
      slideEl.querySelector(SLIDER_SELECTORS.productSlide)?.getAttribute(SLIDER_SELECTORS.dataMediaId)
    );
  }

  _splideIndexForThumb(thumb) {
    const mediaId = thumb.getAttribute(SLIDER_SELECTORS.dataMediaId);
    if (!mediaId) return -1;

    return this._getSplideSlides().findIndex(({ slide }) => this._mediaIdFromSplideSlide(slide) === mediaId);
  }

  _handleClick(event) {
    const thumb = event.target.closest(SLIDER_SELECTORS.productThumb);
    if (!thumb || thumb.hidden) return;

    const index = this._splideIndexForThumb(thumb);
    if (index >= 0) {
      this.splide.go(index);
    }
  }

  _visibleSlideMediaIds() {
    return new Set(
      this._getSplideSlides()
        .map(({ slide }) => this._mediaIdFromSplideSlide(slide))
        .filter(Boolean)
    );
  }

  syncFromSplide() {
    const active = this.splide?.Components?.Slides?.getAt(this.splide.index);
    const activeId = this._mediaIdFromSplideSlide(active?.slide);

    this.items.forEach((item) => {
      const isActive = Boolean(activeId) && item.getAttribute(SLIDER_SELECTORS.dataMediaId) === activeId;
      item.classList.toggle(THUMB_CLASSES.classActive, isActive);
      item.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  refresh(visibleCount) {
    if (!this.host) return;

    this.host.classList.toggle(SLIDER_SELECTORS.classThumbsHidden, visibleCount <= 1);

    const visibleIds = this._visibleSlideMediaIds();

    this.items.forEach((item) => {
      const mediaId = item.getAttribute(SLIDER_SELECTORS.dataMediaId);
      item.hidden = !visibleIds.has(mediaId);
    });

    if (visibleCount > 0) {
      this.syncFromSplide();
    }
  }

  destroy() {
    if (!this.splide || !this.host) return;

    this.splide.off('moved', this._onSplideMoved);
    this.splide.off('mounted', this._onSplideMoved);
    this.host.removeEventListener('click', this._onClick);
  }
}

class ProductMediaSlider {
  constructor(section) {
    this.container = section.container;
    this.tallLayout = this.container.getAttribute(SLIDER_SELECTORS.dataTallLayout) === 'true';
    this.slideshow = this.container.querySelector(SLIDER_SELECTORS.productSlideshow);
    this.thumbs = this.container.querySelector(SLIDER_SELECTORS.productThumbs);
    this.isQuickAdd = Boolean(
      this.container.querySelector('[data-quick-add-gallery]') ||
        this.container.closest('[data-quick-add-modal]')
    );
    this.splide = null;
    this.mediaThumbs = null;
    this._onQuickAddOpen = () => this._refreshAfterLayout();
  }

  init() {
    this._whenSplideReady(() => this.createSlider());

    if (this.isQuickAdd) {
      document.addEventListener('theme:quick-add:open', this._onQuickAddOpen);
    }
  }

  _whenSplideReady(callback) {
    if (window.Splide) {
      callback();
      return;
    }

    const splideScript = document.querySelector('script[src*="vendor-splide"]');

    if (splideScript) {
      splideScript.addEventListener('load', callback, { once: true });
    }

    let attempts = 0;
    const wait = () => {
      if (window.Splide) {
        callback();
        return;
      }

      if (attempts++ < 100) {
        requestAnimationFrame(wait);
      }
    };

    requestAnimationFrame(wait);
  }

  destroy() {
    if (this.isQuickAdd) {
      document.removeEventListener('theme:quick-add:open', this._onQuickAddOpen);
    }
    this.destroySlider();
  }

  _refreshAfterLayout() {
    requestAnimationFrame(() => {
      if (!this.splide) return;
      this.splide.refresh();
      this.splide.go(this.splide.index);
    });
  }

  refreshSlider() {
    if (!this.splide) return;

    const visibleCount = this._syncSplideTrack(
      this.slideshow,
      this.splide,
      SLIDER_SELECTORS.productSlide,
      true
    );

    if (this.mediaThumbs) {
      this.mediaThumbs.refresh(visibleCount);
    }

    this._toggleThumbsHost(visibleCount);

    if (visibleCount > 0) {
      this.splide.go(0);
    }
  }

  destroySlider() {
    if (!this.slideshow) return;

    const isSliderInitialized = this.slideshow.classList.contains(SLIDER_SELECTORS.sliderEnabled);

    if (isSliderInitialized) {
      this._restoreSlidesToList(this.slideshow, SLIDER_SELECTORS.productSlide);

      if (this.mediaThumbs) {
        this.mediaThumbs.destroy();
        this.mediaThumbs = null;
      }

      if (this.splide) {
        this.splide.destroy();
        this.splide = null;
      }
    }
  }

  _getSlidesFromRoot(rootEl, slideSelector) {
    if (!rootEl) return [];
    return Array.from(rootEl.querySelectorAll(slideSelector));
  }

  _getAllSlidesOrdered(rootEl, slideSelector) {
    return this._getSlidesFromRoot(rootEl, slideSelector).sort((a, b) => {
      const orderA = parseInt(a.getAttribute(SLIDER_SELECTORS.dataSlideOrder), 10) || 0;
      const orderB = parseInt(b.getAttribute(SLIDER_SELECTORS.dataSlideOrder), 10) || 0;
      return orderA - orderB;
    });
  }

  _getSplideStash(rootEl) {
    let stash = rootEl.querySelector(SLIDER_SELECTORS.splideStash);

    if (!stash) {
      stash = document.createElement('div');
      stash.setAttribute('data-splide-stash', '');
      stash.hidden = true;
      rootEl.appendChild(stash);
    }

    return stash;
  }

  _restoreSlidesToList(rootEl, slideSelector) {
    const list = rootEl?.querySelector(SLIDER_SELECTORS.splideList);
    if (!list) return;

    this._getAllSlidesOrdered(rootEl, slideSelector).forEach((slide) => {
      list.appendChild(slide);
    });
  }

  _syncSplideTrack(rootEl, splideInstance, slideSelector, isMainGallery = false) {
    if (!rootEl || !splideInstance) return 0;

    const list = rootEl.querySelector(SLIDER_SELECTORS.splideList);
    if (!list) return 0;

    const stash = this._getSplideStash(rootEl);
    const slides = this._getAllSlidesOrdered(rootEl, slideSelector);
    const visibleCount = slides.filter((slide) => !slide.classList.contains(SLIDER_SELECTORS.classHidden)).length;

    slides.forEach((slide) => {
      if (slide.classList.contains(SLIDER_SELECTORS.classHidden)) {
        stash.appendChild(slide);
      } else {
        list.appendChild(slide);
      }
    });

    if (isMainGallery) {
      this._applyMainSplideType(splideInstance, visibleCount);
    }

    splideInstance.refresh();

    return visibleCount;
  }

  _toggleThumbsHost(visibleCount) {
    if (!this.thumbs) return;

    this.thumbs.classList.toggle(SLIDER_SELECTORS.classThumbsHidden, visibleCount <= 1);
  }

  _countVisibleSlides(rootEl, slideSelector) {
    if (!rootEl) return 0;

    return this._getAllSlidesOrdered(rootEl, slideSelector).filter(
      (slide) => !slide.classList.contains(SLIDER_SELECTORS.classHidden)
    ).length;
  }

  _hasThumbCarousel() {
    return Boolean(this.mediaThumbs);
  }

  /** Loop clones break index parity with the thumb nav rail. */
  _applyMainSplideType(splideInstance, visibleCount) {
    const useThumbSync = this._hasThumbCarousel();

    if (visibleCount > 1) {
      splideInstance.options.type = useThumbSync ? 'slide' : 'loop';
      splideInstance.options.rewind = useThumbSync;
    } else {
      splideInstance.options.type = 'slide';
      splideInstance.options.rewind = false;
    }
  }

  _getSlideElements() {
    return this._getSlidesFromRoot(this.slideshow, SLIDER_SELECTORS.productSlide);
  }

  _getSelectedSlideElement() {
    const active = this.splide?.Components?.Slides?.getAt(this.splide.index);
    return active?.slide?.querySelector(SLIDER_SELECTORS.productSlide) || active?.slide;
  }

  _setDraggable(enabled) {
    if (!this.splide) return;

    this.splide.options.drag = enabled;

    if (enabled) {
      this.splide.Components.Drag?.enable?.(true);
    } else {
      this.splide.Components.Drag?.disable?.(true);
    }
  }

  _isInteractiveMediaType(type) {
    return type === 'model' || type === 'video' || type === 'external_video';
  }

  createSlider() {
    if (!this.slideshow || !window.Splide) {
      return;
    }

    if (this.slideshow.classList.contains(SLIDER_SELECTORS.sliderEnabled)) {
      return;
    }

    const instance = this;
    const firstSlide = this.slideshow.querySelectorAll(`[${SLIDER_SELECTORS.mediaType}]`)[0];
    const visibleCount = this._countVisibleSlides(
      this.slideshow,
      SLIDER_SELECTORS.productSlide
    );

    const options = this.isQuickAdd
      ? {
          type: visibleCount > 1 ? 'loop' : 'slide',
          rewind: false,
          autoHeight: true,
          arrows: true,
          pagination: false,
          drag: true,
          gap: 0,
          perPage: 1,
          padding: 0,
          speed: 400,
        }
      : {
          type: visibleCount > 1 ? 'loop' : 'slide',
          rewind: false,
          autoHeight: true,
          arrows: true,
          pagination: false,
          drag: true,
          gap: 10,
          focus: 0,
          trimSpace: false,
          perPage: 1,
          padding: 0,
          breakpoints: {
            750: {
              padding: { right: '9%' },
            },
          },
        };

    this.splide = new window.Splide(this.slideshow, options);

    this.splide.on('mounted', () => instance._dispatchSliderLoaded());
    this.splide.mount();

    if (this.thumbs) {

      this.mediaThumbs = new ProductMediaThumbs({
        splide: this.splide,
        host: this.thumbs,
      });
      this.mediaThumbs.refresh(visibleCount);
    }

    if (firstSlide) {
      const firstType = firstSlide.getAttribute(SLIDER_SELECTORS.mediaType);

      if (instance._isInteractiveMediaType(firstType)) {
        instance._setDraggable(false);
      }
    }

    this.splide.on('move', function (newIndex, prevIndex) {
      const slideComponents = instance.splide.Components.Slides.get();
      const prevMedia = slideComponents[prevIndex]?.slide;
      const newMedia = slideComponents[newIndex]?.slide;

      if (prevMedia) {
        prevMedia.dispatchEvent(new CustomEvent('mediaHidden'));
      }

      if (newMedia) {
        newMedia.classList.remove(SLIDER_SELECTORS.classMediaHidden);
      }
    });

    this.splide.on('moved', function () {
      instance.mediaThumbs?.syncFromSplide();

      const currentMedia = instance._getSelectedSlideElement();
      const slideElements = instance._getSlideElements();
      const otherMedia = slideElements.filter((child) => child !== currentMedia);
      const mediaType = currentMedia?.getAttribute(SLIDER_SELECTORS.mediaType);
      const isFocusEnabled = document.body.classList.contains(SLIDER_SELECTORS.focusEnabled);

      if (instance._isInteractiveMediaType(mediaType)) {
        instance._setDraggable(false);
      } else {
        instance._setDraggable(true);
      }

      if (isFocusEnabled && currentMedia) currentMedia.dispatchEvent(new Event('focus'));

      if (otherMedia.length) {
        otherMedia.forEach((element) => {
          element.classList.add(SLIDER_SELECTORS.classMediaHidden);
        });
      }

      if (currentMedia) {
        currentMedia.dispatchEvent(new CustomEvent('mediaVisible'));

        const deferredMedia = currentMedia.querySelector('deferred-media');
        if (deferredMedia && deferredMedia.getAttribute('loaded') !== true) {
          currentMedia.querySelector(SLIDER_SELECTORS.deferredMediaButton)?.dispatchEvent(new Event('click', { bubbles: false }));
        }
      }
    });

    this.splide.on('drag', () => {
      instance.slideshow.classList.add(SLIDER_CLASSES.classDrag);
    });

    this.splide.on('dragged', () => {
      instance.slideshow.classList.remove(SLIDER_CLASSES.classDrag);
    });
  }

  _dispatchSliderLoaded() {
    this.slideshow.dispatchEvent(new CustomEvent('slider-loaded', { bubbles: true }));
  }
}

class ProductMediaZoom {
  constructor(section) {
    this.container = section.container;
    this.zoomWrappers = this.container.querySelectorAll(ZOOM_SELECTORS.zoomWrapper);
    this.thumbsContainer = document.querySelector(ZOOM_SELECTORS.thumbs);
    this.zoomCaptionElem = this.container.querySelector(ZOOM_SELECTORS.caption);
    this.zoomEnable = this.container.getAttribute(ZOOM_SELECTORS.dataImageZoomEnable) === 'true';
    this._handlers = [];

    if (this.zoomEnable) {
      this.init();
    }
  }

  init() {
    if (this.zoomWrappers.length) {
      this.zoomWrappers.forEach((element, i) => {
        const onClick = (e) => {
          e.preventDefault();
          this.createZoom(i);
        };

        const onKeyup = (e) => {
          if (e.keyCode === window.theme.keyboardKeys.ENTER) {
            e.preventDefault();
            this.createZoom(i);
          }
        };

        element.addEventListener('click', onClick);
        element.addEventListener('keyup', onKeyup);
        this._handlers.push({ element, onClick, onKeyup });
      });
    }
  }

  destroy() {
    this._handlers.forEach(({ element, onClick, onKeyup }) => {
      element.removeEventListener('click', onClick);
      element.removeEventListener('keyup', onKeyup);
    });
    this._handlers = [];
  }

  createZoom(indexImage) {
    const self = this;
    let items = [];
    let counter = 0;
    let thumbs = '';
    const clickedElement = this.zoomWrappers[indexImage];
    let activeIndex = 0;

    this.zoomWrappers.forEach((elementImage) => {
      const isVisible = elementImage.offsetParent !== null;
      if (isVisible) {
        const imgSrc = elementImage.getAttribute(ZOOM_SELECTORS.dataImageSrc);
        const imgWidth = parseInt(elementImage.getAttribute(ZOOM_SELECTORS.dataImageWidth), 10);
        const imgHeight = parseInt(elementImage.getAttribute(ZOOM_SELECTORS.dataImageHeight), 10);

        items.push({
          src: imgSrc,
          w: imgWidth,
          h: imgHeight,
          msrc: imgSrc,
        });

        thumbs += `<a href="#" class="${ZOOM_CLASSES.popupThumb}" style="background-image: url('${imgSrc}')"></a>`;

        if (elementImage === clickedElement) {
          activeIndex = items.length - 1;
        }
      }

      counter += 1;
      if (self.zoomWrappers.length === counter) {
        let popupClass = `${ZOOM_CLASSES.popupClass}`;
        if (counter === 1) {
          popupClass = `${ZOOM_CLASSES.popupClass} ${ZOOM_CLASSES.popupClassNoThumbs}`;
        }
        const options = {
          history: false,
          focus: false,
          index: activeIndex,
          mainClass: popupClass,
          showHideOpacity: true,
          howAnimationDuration: 150,
          hideAnimationDuration: 250,
          closeOnScroll: false,
          closeOnVerticalDrag: false,
          captionEl: true,
          closeEl: true,
          closeElClasses: ['caption-close', 'title'],
          tapToClose: false,
          clickToCloseNonZoomable: false,
          maxSpreadZoom: 2,
          wrapAround: true,
          spacing: 0,
          allowPanToNext: true,
          pinchToClose: false,
          addCaptionHTMLFn: function (item, captionEl, isFake) {
            self.zoomCaption(item, captionEl, isFake);
          },
          getThumbBoundsFn: function getThumbBoundsFn() {
            const imageLocation = self.zoomWrappers[indexImage];
            const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
            const rect = imageLocation.getBoundingClientRect();
            return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
          },
        };

        new window.theme.LoadPhotoswipe(items, options);

        if (self.thumbsContainer && thumbs !== '') {
          self.thumbsContainer.innerHTML = thumbs;
        }
      }
    });
  }

  zoomCaption(item, captionEl) {
    let captionHtml = '';
    const targetContainer = captionEl.children[0];
    if (this.zoomCaptionElem) {
      captionHtml = this.zoomCaptionElem.innerHTML;

      if (this.zoomCaptionElem.closest(`.${ZOOM_CLASSES.variantSoldOut}`)) {
        targetContainer.classList.add(ZOOM_CLASSES.variantSoldOut);
      } else {
        targetContainer.classList.remove(ZOOM_CLASSES.variantSoldOut);
      }

      if (this.zoomCaptionElem.closest(`.${ZOOM_CLASSES.variantUnavailable}`)) {
        targetContainer.classList.add(ZOOM_CLASSES.variantUnavailable);
      } else {
        targetContainer.classList.remove(ZOOM_CLASSES.variantUnavailable);
      }
    }

    captionHtml = captionHtml.replaceAll(ZOOM_CLASSES.popupTitle, ZOOM_CLASSES.popupTitleNew);
    targetContainer.innerHTML = captionHtml;
    return false;
  }
}

class ProductMediaVideo {
  constructor(section) {
    this.container = section.container;
    this.id = section.id;
    this.players = {};
    this._deferredHandlers = [];
    this.init();
  }

  init() {
    const mediaContainers = this.container.querySelectorAll(VIDEO_SELECTORS.mediaContainer);

    mediaContainers.forEach((mediaContainer) => {
      const deferredMediaButton = mediaContainer.querySelector(VIDEO_SELECTORS.deferredMediaButton);

      if (deferredMediaButton) {
        const handler = this.loadContent.bind(this, mediaContainer);
        deferredMediaButton.addEventListener('click', handler);
        this._deferredHandlers.push({ deferredMediaButton, handler });
      }
    });
  }

  destroy() {
    this._deferredHandlers.forEach(({ deferredMediaButton, handler }) => {
      deferredMediaButton.removeEventListener('click', handler);
    });
    this._deferredHandlers = [];
  }

  loadContent(mediaContainer) {
    if (mediaContainer.querySelector(VIDEO_SELECTORS.deferredMedia).getAttribute('loaded')) {
      return;
    }

    const content = document.createElement('div');
    content.appendChild(mediaContainer.querySelector('template').content.firstElementChild.cloneNode(true));
    const mediaId = mediaContainer.dataset.mediaId;
    const element = content.querySelector('video, iframe');
    const host = this.hostFromVideoElement(element);
    const deferredMedia = mediaContainer.querySelector(VIDEO_SELECTORS.deferredMedia);
    deferredMedia.appendChild(element).focus();
    deferredMedia.setAttribute('loaded', true);

    this.players[mediaId] = {
      mediaId: mediaId,
      sectionId: this.id,
      container: mediaContainer,
      element: element,
      host: host,
      ready: () => {
        this.createPlayer(mediaId);
      },
    };

    const video = this.players[mediaId];

    switch (video.host) {
      case VIDEO_HOSTS.html5:
        this.loadVideo(video, VIDEO_HOSTS.html5);
        break;
      case VIDEO_HOSTS.youtube:
        if (window.isYoutubeAPILoaded) {
          this.loadVideo(video, VIDEO_HOSTS.youtube);
        } else {
          window.theme.loadScript({ url: 'https://www.youtube.com/iframe_api' }).then(() => this.loadVideo(video, VIDEO_HOSTS.youtube));
        }
        break;
    }
  }

  hostFromVideoElement(video) {
    if (video.tagName === 'VIDEO') {
      return VIDEO_HOSTS.html5;
    }

    if (video.tagName === 'IFRAME') {
      if (/^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com|youtu\.?be)\/.+$/.test(video.src)) {
        return VIDEO_HOSTS.youtube;
      }
    }
    return null;
  }

  loadVideo(video, host) {
    if (video.host === host) {
      video.ready();
    }
  }

  createPlayer(mediaId) {
    const video = this.players[mediaId];
    const enableLooping = video.container.dataset.enableVideoLooping;

    switch (video.host) {
      case VIDEO_HOSTS.html5:
        video.element.addEventListener('play', () => {
          this.pauseOtherMedia(mediaId);
        });

        video.element.play();
        video.container.addEventListener('mediaHidden', (event) => this.onHidden(event));
        video.container.addEventListener('xrLaunch', (event) => this.onHidden(event));
        video.container.addEventListener('mediaVisible', (event) => this.onVisible(event));

        break;

      case VIDEO_HOSTS.youtube:
        if (video.host == VIDEO_HOSTS.youtube && video.player) {
          return;
        }

        YT.ready(() => {
          const videoId = video.container.dataset.videoId;

          this.players[mediaId].player = new YT.Player(video.element, {
            videoId: videoId,
            events: {
              onReady: (event) => {
                event.target.playVideo();
              },
              onStateChange: (event) => {
                if (event.data === 0) {
                  if (enableLooping) {
                    event.target.seekTo(0);
                  }
                }
                if (event.data === 1) {
                  this.pauseOtherMedia(mediaId);
                }
                if (event.data === 2);
              },
            },
          });

          window.isYoutubeAPILoaded = true;

          video.container.addEventListener('mediaHidden', (event) => this.onHidden(event));
          video.container.addEventListener('xrLaunch', (event) => this.onHidden(event));
          video.container.addEventListener('mediaVisible', (event) => this.onVisible(event));
        });

        break;
    }
  }

  onHidden(event) {
    if (typeof event.target.dataset.mediaId !== 'undefined') {
      const mediaId = event.target.dataset.mediaId;
      const videoPlayer = this.players[mediaId];

      if (videoPlayer.player && videoPlayer.player.pauseVideo) {
        videoPlayer.player.pauseVideo();
      } else if (videoPlayer.element && videoPlayer.element.pause) {
        videoPlayer.element.pause();
      }
    }
  }

  onVisible(event) {
    if (typeof event.target.dataset.mediaId !== 'undefined') {
      const mediaId = event.target.dataset.mediaId;
      const videoPlayer = this.players[mediaId];

      if (videoPlayer.player && videoPlayer.player.playVideo) {
        videoPlayer.player.playVideo();
      } else if (videoPlayer.element && videoPlayer.element.play) {
        videoPlayer.element.play();
      }
    }
  }

  pauseOtherMedia(mediaId) {
    const mediaIdString = `[${VIDEO_SELECTORS.mediaId}="${mediaId}"]`;
    const currentMedia = document.querySelector(`${VIDEO_SELECTORS.productMediaWrapper}${mediaIdString}`);
    const otherMedia = document.querySelectorAll(`${VIDEO_SELECTORS.productMediaWrapper}:not(${mediaIdString})`);
    currentMedia.classList.remove('media--hidden');

    if (otherMedia.length) {
      otherMedia.forEach((element) => {
        element.dispatchEvent(new CustomEvent('mediaHidden'));
        element.classList.add('media--hidden');
      });
    }
  }
}

if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    connectedCallback() {
      this.container =
        this.closest('[data-section-type="product"]') ||
        this.closest('[data-section-type="api-product-upsell"]') ||
        this.closest('[data-quick-add-modal]');

      const modal = this.closest('[data-quick-add-modal]');
      this.id = modal?.id || this.container?.dataset.sectionId;

      if (!this.container || !this.id) return;

      window.theme.mediaInstances = window.theme.mediaInstances || {};
      window.theme.mediaInstances[this.id] = this;

      const section = { container: this.container, id: this.id };

      this.detect3d();
      this._onXrLaunch = () => {
        const currentMedia = this.container.querySelector(
          `${MODEL_SELECTORS.modelViewer}:not(.${MODEL_SELECTORS.classMediaHidden})`
        );
        if (currentMedia) {
          currentMedia.dispatchEvent(new CustomEvent('xrLaunch'));
        }
      };
      document.addEventListener('shopify_xr_launch', this._onXrLaunch);

      this._video = new ProductMediaVideo(section);
      this._zoom = new ProductMediaZoom(section);
      this._slider = new ProductMediaSlider(section);
      this._slider.init();
    }

    disconnectedCallback() {
      if (this._slider) {
        this._slider.destroy();
      }
      if (this._zoom) {
        this._zoom.destroy();
      }
      if (this._video) {
        this._video.destroy();
      }
      if (this._onXrLaunch) {
        document.removeEventListener('shopify_xr_launch', this._onXrLaunch);
      }
      if (this.id && window.theme.mediaInstances) {
        delete window.theme.mediaInstances[this.id];
      }
    }

    refreshSlider() {
      this._slider?.refreshSlider();
    }

    detect3d() {
      const modelViewerElements = this.container.querySelectorAll(MODEL_SELECTORS.modelViewer);
      if (modelViewerElements.length) {
        modelViewerElements.forEach((element) => {
          window.theme.ProductModel.init(element, this.id);
        });
      }
    }
  }

  customElements.define('product-media', ProductMedia);
}
